import graphql from "babel-plugin-relay/macro";
import { useAtomValue } from "jotai";
import React from "react";
import { useFragment } from "react-relay";

import { Loader } from "@whoosh/design-system/src/components/display";

import { currentEventSignUpStepAtom } from "../atoms/eventSignUpProcess";
import { AddPeopleRenderFunction } from "../types";
import { AddPeopleStep } from "./AddPeopleStep";
import { ErrorScreen } from "./ErrorScreen";
import { EventFormStep } from "./EventFormStep";
import { EventSignUpPrimaryChargePaymentAssignment } from "./EventSignUpPrimaryChargePaymentAssignment";
import { PrimaryChargeSelectionStep } from "./PrimaryChargeSelectionStep/PrimaryChargeSelectionStep";
import { FormStepManager_event$key } from "./__generated__/FormStepManager_event.graphql";

type FormStepManagerProps = {
  addPeopleRenderFunction: AddPeopleRenderFunction;
  eventRef: FormStepManager_event$key;
  onCancelCallback: () => void;
};

export const FormStepManager = ({
  addPeopleRenderFunction,
  eventRef,
  onCancelCallback,
}: FormStepManagerProps) => {
  const currentFormStep = useAtomValue(currentEventSignUpStepAtom);
  const event = useFragment(FormStepManagerFragmentString, eventRef);

  const maxGroupSize = event.maxMemberCount + event.maxGuestCount;

  // In order to display the correct number of spots available, we need to account for the capacity type.
  // In order for capacity limits or group size limits to work, we need to account for null spots available as well.

  const spotsAvailable =
    event.capacityType === "ATTENDEES"
      ? event.spotsAvailable
      : !event.spotsAvailable
      ? null
      : (event?.spotsAvailable || 0) * (maxGroupSize || 0);

  if (currentFormStep === null) return <Loader />;

  switch (currentFormStep) {
    case "PEOPLE":
      return (
        <AddPeopleStep
          groupRegistrationDisplayFragmentRef={event}
          addPeopleRenderFunction={addPeopleRenderFunction}
          spotsAvailable={spotsAvailable ?? null}
          allowedGroupSizes={event.allowedGroupSizes}
        />
      );
    case "FORM":
      return <EventFormStep eventRef={event} />;
    case "PRIMARY_CHARGE_SELECTION":
      return <PrimaryChargeSelectionStep eventRef={event} />;
    case "PRIMARY_CHARGE_WHO_PAYS":
      return (
        <EventSignUpPrimaryChargePaymentAssignment
          eventRef={event}
          primaryChargeType={event.primaryChargeType ?? null}
        />
      );
    default:
      return <ErrorScreen onCancelCallback={onCancelCallback} />;
  }
};

const FormStepManagerFragmentString = graphql`
  fragment FormStepManager_event on Event {
    ...GroupRegistrationDisplay_event
    ...EventFormStep_event
    ...PrimaryChargeSelectionStep_event
    ...EventSignUpPrimaryChargePaymentAssignment_event
    primaryChargeType
    prePaymentRequirement
    spotsAvailable
    memberSignUpProxyAllowed
    capacityType
    maxMemberCount
    maxGuestCount
    allowedGroupSizes
  }
`;
